import { default as React } from 'react';
import { Entity } from '@backstage/catalog-model';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Button } from '@material-ui/core';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';

const useStyles = makeStyles((theme: Theme) => ({
  iconButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    opacity: 0.5,
    '&:hover': {
      color: theme.palette.error.main,
      opacity: 1,
    },
  },
}));

/**
 * Icon button + confirmation dialog that deletes a public app.
 * Shared by the table (Actions column) and the card grid (IDP-1291).
 * Callers render it only for owners / SkyStage admins.
 */
export const PublicAppDeleteButton = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const [open, setOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const appName = entity.metadata.name;

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('microapps-api');
      const response = await fetchApi.fetch(
        `${baseUrl}/unregister-public-app`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appName,
            entityKind: entity.kind,
            entityNamespace: entity.metadata.namespace ?? 'default',
          }),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ??
            `Server error ${response.status}`,
        );
      }
      alertApi.post({
        message: `${appName} deleted successfully. Refreshing…`,
        severity: 'success',
        display: 'transient',
      });
      setOpen(false);
      // Reload after a short delay so the catalog processor has time to remove the entity
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: unknown) {
      alertApi.post({
        message: `Failed to delete ${appName}: ${
          err instanceof Error ? err.message : String(err)
        }`,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Tooltip title={`Delete ${appName}`}>
        <IconButton
          className={classes.iconButton}
          size="small"
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
          aria-label={`delete ${appName}`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => !deleting && setOpen(false)}
        onClick={e => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Public App
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{appName}</strong>? This will remove
            ArgoCD resources, CIAM access group, container images, and the
            catalog entry. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleConfirm}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
